/**
 * The quiet dot after a row's text: something someone else added in the last few days that
 * you haven't seen yet. It counts as seen once the row has been on screen for a moment (or you
 * opened it); it stays put until you leave the page, so it doesn't vanish under your eyes.
 * Per person: user settings `newSince` (when this started — older things are never "new")
 * and `newSeen` (ids seen since).
 */
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { ACTIVITY_DAYS, activityLevels } from '@terrazzo/core'
import { store } from '../store/store'
import { actions } from '../store/actions'
import { myId } from '../store/me'
import { sync } from '../sync/doc'

const WINDOW = ACTIVITY_DAYS * 86400_000
let cache: Set<string> | null = null
const invalidate = () => {
  cache = null
}
store.subscribeContent(invalidate)
store.subscribeMeta(invalidate)

function newIds(): Set<string> {
  if (cache) return cache
  const out = new Set<string>()
  const me = myId()
  const s = store.userSettings(me)
  const since = Math.max(Date.now() - WINDOW, Number(s.newSince) || Number.MAX_SAFE_INTEGER)
  if (activityLevels(s).added !== 'off') {
    const seen = new Set(Array.isArray(s.newSeen) ? (s.newSeen as string[]) : [])
    for (const it of store.idx.items.values()) {
      if (it.createdAt <= since || it.system || it.createdBy === me || it.createdBy === 'system' || seen.has(it.id)) continue
      if (store.tree.inTrash.has(it.id) || store.tree.inArchive.has(it.id)) continue
      out.add(it.id)
    }
  }
  return (cache = out)
}

/** Start counting from now the first time (so nothing older lights up). */
export function startNewMarks() {
  const go = () => {
    if (!sync.getStatus().synced || myId() === 'unknown') return false
    if (!store.userSettings(myId()).newSince) actions.setMine('newSince', Date.now())
    return true
  }
  if (go()) return
  const off = sync.subscribe(() => go() && off())
}

let pending = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | null = null
export function markSeen(id: string) {
  if (!newIds().has(id)) return
  pending.add(id)
  flushTimer ??= setTimeout(() => {
    flushTimer = null
    const s = store.userSettings(myId())
    const cutoff = Date.now() - WINDOW
    // Keep only ids that could still be "new" (the list stays small).
    const keep = (Array.isArray(s.newSeen) ? (s.newSeen as string[]) : []).filter((x) => (store.get(x)?.createdAt ?? 0) > cutoff)
    actions.setMine('newSeen', [...new Set([...keep, ...pending])])
    pending = new Set()
  }, 800)
}

const subscribe = (cb: () => void) => {
  const a = store.subscribeContent(cb)
  const b = store.subscribeMeta(cb)
  return () => {
    a()
    b()
  }
}

export function useIsNew(id: string): boolean {
  return useSyncExternalStore(subscribe, () => newIds().has(id))
}

export function NewMark({ id }: { id: string }) {
  const isNew = useIsNew(id)
  const shown = useRef(false)
  if (isNew) shown.current = true
  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!isNew || !el || typeof IntersectionObserver === 'undefined') return
    let t: ReturnType<typeof setTimeout> | undefined
    const io = new IntersectionObserver(
      ([en]) => {
        clearTimeout(t)
        if (en?.isIntersecting && document.visibilityState === 'visible') t = setTimeout(() => markSeen(id), 1500)
      },
      { threshold: 0.5 },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      clearTimeout(t)
    }
  }, [isNew, id])
  if (!shown.current) return null
  return <sup ref={ref} className="new-mark" data-new-mark={id} aria-label="New" />
}
